import { Suspense, Component, useMemo, useRef, type ReactNode } from "react";
import { useFrame } from "@react-three/fiber";
import { useGLTF } from "@react-three/drei";
import * as THREE from "three";
import { CourierModel } from "./models";
import type { VehicleDef } from "./vehicles";

/** Target footprint length (world units) so every car fills the stage the same. */
const CAR_TARGET_LEN = 1.95;
/** Hard cap so tall trucks / roof lights never poke out of the frame. */
const CAR_TARGET_MAX_HEIGHT = 1.18;

type Palette = Pick<VehicleDef, "color" | "emissive" | "trail">;

class ModelErrorBoundary extends Component<
  { fallback: ReactNode; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

/** Sketchfab transport packs ship invisible physics "Collider" meshes that
 * inflate the AABB and make the visible car float above the podium. */
function stripNonVisualMeshes(root: THREE.Object3D) {
  const doomed: THREE.Object3D[] = [];
  root.traverse((child) => {
    const name = (child.name || "").toLowerCase();
    if (name.includes("collider") || name.includes("collision")) {
      doomed.push(child);
      return;
    }
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const m of mats) {
      const matName = ((m as THREE.Material)?.name || "").toLowerCase();
      if (matName.includes("collider") || matName.includes("collision")) {
        doomed.push(child);
        return;
      }
    }
  });
  for (const obj of doomed) {
    obj.parent?.remove(obj);
  }
}

function measureBox(root: THREE.Object3D): THREE.Box3 {
  root.updateMatrixWorld(true);
  const box = new THREE.Box3();
  const tmp = new THREE.Box3();
  let any = false;
  root.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh || !mesh.geometry) return;
    if (!mesh.visible) return;
    // Skip transparent / glass / shadow-decal meshes — Sketchfab `_tr`
    // packs often include a flat ground blob that sits below the tires
    // and makes the car look airborne when used for grounding.
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    const mostlyTransparent = mats.every((m) => {
      const mat = m as THREE.MeshStandardMaterial;
      return !!mat && (mat.transparent === true || (mat.opacity ?? 1) < 0.99);
    });
    const name = (mesh.name || "").toLowerCase();
    if (mostlyTransparent || name.includes("_tr") || name.includes("shadow")) {
      return;
    }
    mesh.geometry.computeBoundingBox();
    if (!mesh.geometry.boundingBox) return;
    tmp.copy(mesh.geometry.boundingBox).applyMatrix4(mesh.matrixWorld);
    const size = new THREE.Vector3();
    tmp.getSize(size);
    // Ignore near-flat filler (shadow cards, decals).
    if (size.y < 0.02 && size.x * size.z > 0.1) return;
    if (!any) {
      box.copy(tmp);
      any = true;
    } else {
      box.union(tmp);
    }
  });
  if (!any) box.setFromObject(root);
  return box;
}

function normalizeCarScene(
  scene: THREE.Object3D,
  groundBias = 0,
  /** Garage podium uses X as the long axis; the track uses Z. */
  axis: "x" | "z" = "x",
): THREE.Object3D {
  const clone = scene.clone(true);
  stripNonVisualMeshes(clone);

  let box = measureBox(clone);
  let size = new THREE.Vector3();
  box.getSize(size);

  // Sketchfab cars sometimes export standing on the nose (Y = length).
  if (size.y > size.x && size.y > size.z) {
    clone.rotation.x -= Math.PI / 2;
    box = measureBox(clone);
    box.getSize(size);
  }

  if (axis === "x") {
    // Garage: longest axis along X.
    if (size.z >= size.x) {
      clone.rotation.y = Math.PI / 2;
      box = measureBox(clone);
      box.getSize(size);
    }
  } else {
    // Track: longest axis along Z, nose toward -Z (same as rivals).
    if (size.x > size.z) {
      clone.rotation.y += Math.PI / 2;
      box = measureBox(clone);
      box.getSize(size);
    }
    clone.rotation.y += Math.PI;
    box = measureBox(clone);
    box.getSize(size);
  }

  const center = new THREE.Vector3();
  box.getCenter(center);
  const lengthScale = CAR_TARGET_LEN / Math.max(size.x, size.z, 0.001);
  const heightScale = CAR_TARGET_MAX_HEIGHT / Math.max(size.y, 0.001);
  const scale = Math.min(lengthScale, heightScale);

  clone.scale.setScalar(scale);
  clone.position.set(-center.x * scale, -box.min.y * scale, -center.z * scale);

  box = measureBox(clone);
  clone.position.y -= box.min.y;
  clone.position.y -= groundBias;

  clone.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
  });

  return clone;
}

function applyGhost(root: THREE.Object3D, ghost: boolean) {
  root.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.castShadow = !ghost;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const m of mats) {
      const mat = m as THREE.MeshStandardMaterial;
      if (!mat || !("opacity" in mat)) continue;
      if (mat.userData._baseOpacity == null) {
        mat.userData._baseOpacity = mat.opacity;
        mat.userData._baseEmissiveIntensity = mat.emissiveIntensity ?? 0;
        mat.userData._baseWireframe = Boolean(mat.wireframe);
      }
      if (ghost) {
        mat.transparent = true;
        mat.opacity = 0.16;
        mat.depthWrite = false;
        mat.wireframe = true;
        mat.emissiveIntensity = Math.min(
          0.35,
          Number(mat.userData._baseEmissiveIntensity) || 0.3,
        );
      } else {
        mat.opacity = mat.userData._baseOpacity;
        mat.transparent = mat.userData._baseOpacity < 1;
        mat.depthWrite = true;
        mat.wireframe = mat.userData._baseWireframe;
        mat.emissiveIntensity = mat.userData._baseEmissiveIntensity;
      }
      mat.needsUpdate = true;
    }
  });
}

function CarGltf({
  modelPath,
  ghost,
  groundBias,
  axis,
}: {
  modelPath: string;
  ghost: boolean;
  groundBias: number;
  axis: "x" | "z";
}) {
  const { scene } = useGLTF(modelPath);
  const model = useMemo(
    () => normalizeCarScene(scene, groundBias, axis),
    [scene, groundBias, axis],
  );

  useMemo(() => {
    applyGhost(model, ghost);
  }, [model, ghost]);

  return <primitive object={model} />;
}

function ProceduralFallback({
  palette,
  ghost,
}: {
  palette: Palette;
  ghost: boolean;
}) {
  return <CourierModel skin={palette} ghost={ghost} />;
}

/** Renders the equipped / preview vehicle (GLB or procedural hoverbike). */
export function VehicleModel({
  vehicle,
  ghost = false,
  spin = false,
  /** Extra Y sink — use in garage so tires sit on the podium. */
  groundBias = 0,
  /** `z` = on-track (length along the road). `x` = garage podium. */
  axis = "x",
}: {
  vehicle: VehicleDef;
  ghost?: boolean;
  /** Slow Y spin for garage podium. */
  spin?: boolean;
  groundBias?: number;
  axis?: "x" | "z";
}) {
  const groupRef = useRef<THREE.Group>(null);
  const fallback = <ProceduralFallback palette={vehicle} ghost={ghost} />;

  useFrame((_, dt) => {
    if (!spin || !groupRef.current) return;
    groupRef.current.rotation.y += dt * 0.55;
  });

  if (vehicle.model === "procedural") {
    return (
      <group ref={groupRef}>
        <CourierModel skin={vehicle} ghost={ghost} />
      </group>
    );
  }

  return (
    <group ref={groupRef}>
      <ModelErrorBoundary fallback={fallback}>
        <Suspense fallback={fallback}>
          <CarGltf
            modelPath={vehicle.model}
            ghost={ghost}
            groundBias={groundBias}
            axis={axis}
          />
        </Suspense>
      </ModelErrorBoundary>
    </group>
  );
}

/** Preload fleet GLBs once the module loads. */
if (typeof window !== "undefined") {
  for (const v of [
    "/models/low_poly_car_1.glb",
    "/models/low_poly_car_4.glb",
    "/models/low_poly_car_6.glb",
    "/models/super_car.glb",
  ]) {
    try {
      useGLTF.preload(v);
    } catch {
      /* WebGL unavailable */
    }
  }
}
